import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import CommonFooter from '../../common_components/CommonFooter';
/* ─── design tokens (matches project theme) ──────────────────────────────── */
const BLUE = '#006FEE';
const BLUE_LIGHT = '#EFF6FF';
const BLUE_MID = '#CCE3FD';
const PINK = '#FF008E';
const BG = '#F8F9FC';
const SURFACE = '#FFFFFF';
const BORDER = '#E9EAEB';
const T1 = '#181D27';
const T2 = '#717680';
const T3 = '#98A2B3';
const SUCCESS = '#12B76A';
const SUCCESS_BG = '#ECFDF3';
/* ─────────────────────────────────────────────────────────────────────────── */
const FEATURES = [
    { Icon: ShoppingBagRoundedIcon, label: 'Revenue & Orders', desc: 'Gross sales, net revenue, order volume synced in real-time' },
    { Icon: GroupsRoundedIcon, label: 'Customer Analytics', desc: 'LTV, retention rates and cohort analysis across all channels' },
    { Icon: BarChartRoundedIcon, label: 'Performance Metrics', desc: 'ROAS, AOV, CVR and 10+ Shopify KPIs available in your dashboard' },
];
function parseDomain(raw) {
    try {
        return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace('www.', '');
    }
    catch {
        return raw;
    }
}
/* animated svg checkmark */
function AnimatedCheck({ done }) {
    return (_jsx("svg", { width: "36", height: "36", viewBox: "0 0 36 36", fill: "none", children: _jsx("path", { d: "M7 18L14 25.5L29 10.5", stroke: "white", strokeWidth: "3", strokeLinecap: "round", strokeLinejoin: "round", strokeDasharray: "36", strokeDashoffset: done ? 0 : 36, style: { transition: 'stroke-dashoffset 0.5s 0.2s cubic-bezier(0.65,0,0.35,1)' } }) }));
}
export default function ShopifyConnectionComplete() {
    const [params] = useSearchParams();
    const [mounted, setMounted] = useState(false);
    const [checkAnim, setCheckAnim] = useState(false);
    const rawStore = params.get('store') || '';
    const clientName = params.get('client') || '';
    useEffect(() => {
        const t1 = setTimeout(() => setMounted(true), 80);
        const t2 = setTimeout(() => setCheckAnim(true), 500);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);
    return (_jsxs(Box, { sx: {
            minHeight: '100vh',
            backgroundColor: BG,
            fontFamily: 'Inter, system-ui, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            /* subtle dot grid background */
            backgroundImage: `radial-gradient(circle, ${BORDER} 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
        }, children: [_jsx(Box, { sx: {
                    height: 60,
                    backgroundColor: SURFACE,
                    borderBottom: `1px solid ${BORDER}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: { xs: 3, sm: '40px' },
                    flexShrink: 0,
                    boxShadow: '0px 1px 2px rgba(10,13,18,0.04)',
                }, children: _jsx(Box, { component: "img", src: "/assets/loginImage/logo-final.svg", alt: "Group8A", sx: { height: 28 } }) }), _jsx(Box, { sx: {
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 5,
                }, children: _jsx(Box, { sx: {
                        width: '100%',
                        maxWidth: 350,
                        opacity: mounted ? 1 : 0,
                        transform: mounted ? 'translateY(0)' : 'translateY(24px)',
                        transition: 'opacity 0.5s ease, transform 0.5s ease',
                    }, children: _jsxs(Box, { sx: { textAlign: 'center', mb: 3.5 }, children: [_jsxs(Box, { sx: { display: 'inline-flex', position: 'relative', mb: 2.5 }, children: [_jsx(Box, { sx: {
                                            position: 'absolute',
                                            inset: -8,
                                            borderRadius: '50%',
                                            border: `1.5px solid ${PINK}`,
                                            opacity: checkAnim ? 0 : 0.5,
                                            transform: checkAnim ? 'scale(1.5)' : 'scale(1)',
                                            transition: 'opacity 0.8s 0.6s ease, transform 0.8s 0.6s ease',
                                            pointerEvents: 'none',
                                        } }), _jsx(Box, { sx: {
                                            width: 72,
                                            height: 72,
                                            borderRadius: '50%',
                                            backgroundColor: checkAnim ? PINK : SURFACE,
                                            border: `2px solid ${checkAnim ? PINK : BORDER}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'background-color 0.4s ease, border-color 0.4s ease',
                                            boxShadow: checkAnim
                                                ? `0 0 0 6px #ffe6f3, 0 8px 24px ${PINK}33`
                                                : 'none',
                                        }, children: _jsx(AnimatedCheck, { done: checkAnim }) })] }), _jsx(Typography, { sx: {
                                    fontWeight: 700,
                                    color: T1,
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.4px',
                                    mb: 1.5,
                                }, className: "head-Font-Sizes", children: "App connected successfully." }), _jsxs(Typography, { sx: { fontSize: '0.95rem', color: T2, lineHeight: 1.6 }, children: [clientName || "Your", " Shopify store has been successfully connected to the Group 8A Reporting Dashboard."] })] }) }) }), _jsx(CommonFooter, {})] }));
}
